import { useState, useEffect } from 'react';
import { Cloud, CloudOff, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { getSyncStatus, getPendingItems, getPendingPhotos, updateItemStatus, updatePhotoStatus, deleteItem, deletePhoto } from '../services/offlineSync';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SyncIndicator = ({ getAuthHeaders }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({ total: 0, pendingItems: 0, pendingPhotos: 0, failedItems: 0 });
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      // Trigger sync when coming back online
      syncPendingItems();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check sync status periodically
    checkSyncStatus();
    const interval = setInterval(checkSyncStatus, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const checkSyncStatus = async () => {
    try {
      const status = await getSyncStatus();
      setSyncStatus(status);
    } catch (e) {
      console.error('Failed to check sync status:', e);
    }
  };

  const syncPendingItems = async () => {
    if (!isOnline || syncing) return;

    setSyncing(true);
    
    try {
      // Get pending items
      const pendingItems = await getPendingItems();
      const pendingPhotos = await getPendingPhotos();

      // Sync regular items first
      for (const item of pendingItems) {
        if (item.retries >= 5) {
          await updateItemStatus(item.id, 'failed');
          continue;
        }

        try {
          let endpoint;
          switch (item.type) {
            case 'scouting_point':
              endpoint = `${API}/fields/${item.data.fieldId}/scouting-points`;
              break;
            case 'activity':
              endpoint = `${API}/fields/${item.data.fieldId}/activities`;
              break;
            case 'task':
              endpoint = `${API}/farms/${item.data.farmId}/tasks`;
              break;
            default:
              continue;
          }

          const response = await axios.post(endpoint, item.data, { headers: getAuthHeaders() });
          
          // Update status and store server ID
          await updateItemStatus(item.id, 'synced', response.data.id);
          
          // Clean up synced item after a delay
          setTimeout(async () => {
            await deleteItem(item.id);
          }, 5000);
        } catch (e) {
          console.error('Sync failed for item:', item.id, e);
          await updateItemStatus(item.id, 'pending'); // Will increment retry count
        }
      }

      // Sync photos
      for (const photo of pendingPhotos) {
        if (photo.retries >= 5) {
          await updatePhotoStatus(photo.id, 'failed');
          continue;
        }

        try {
          // Upload photo to server
          const formData = new FormData();
          formData.append('file', photo.blob);
          formData.append('parentId', photo.parentId);
          formData.append('metadata', JSON.stringify(photo.metadata));

          const response = await axios.post(
            `${API}/upload/scouting-photo`,
            formData,
            { 
              headers: { 
                ...getAuthHeaders(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          await updatePhotoStatus(photo.id, 'synced', response.data.storagePath);
          
          setTimeout(async () => {
            await deletePhoto(photo.id);
          }, 5000);
        } catch (e) {
          console.error('Photo sync failed:', photo.id, e);
          await updatePhotoStatus(photo.id, 'pending');
        }
      }

      // Refresh status
      await checkSyncStatus();
    } catch (e) {
      console.error('Sync process error:', e);
    } finally {
      setSyncing(false);
    }
  };

  // Render based on status
  if (!isOnline) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm">
        <CloudOff className="w-4 h-4" />
        <span>Offline</span>
        {syncStatus.total > 0 && (
          <span className="px-1.5 bg-amber-200 rounded-full text-xs">
            {syncStatus.total} pending
          </span>
        )}
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Syncing...</span>
      </div>
    );
  }

  if (syncStatus.failedItems > 0) {
    return (
      <button 
        onClick={syncPendingItems}
        className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm hover:bg-red-200 transition"
      >
        <AlertTriangle className="w-4 h-4" />
        <span>{syncStatus.failedItems} failed</span>
      </button>
    );
  }

  if (syncStatus.total > 0) {
    return (
      <button 
        onClick={syncPendingItems}
        className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm hover:bg-amber-200 transition"
      >
        <Cloud className="w-4 h-4" />
        <span>{syncStatus.total} pending</span>
      </button>
    );
  }

  // All synced
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm">
      <Check className="w-4 h-4" />
      <span>All synced</span>
    </div>
  );
};

export default SyncIndicator;
